import { TakeEffect, takeEvery } from 'redux-saga/effects';
import { createSelector } from 'reselect';
import { CallbackSaga, createGeneralCall } from '@altermaind/core/requests-status-management';
import { IS_MOCK_ENABLED } from '@/_shared/constants/mock';
import { getCurrentOrganization } from '@/core/state/selectors';
import { Filter, SkillCategory } from '@/swagger/constants/contents-orchestrator-enums';
import { API_GET_MY_CONTENTS, KEY_GET_CONTENTS_IN_PROGRESS } from '../constants';
import { MOCK_RESPONSE_GET_CONTENTS_IN_PROGRESS } from '../mocks';
import {
  API_GET_MY_CONTENTS_REQUEST_PATH_PARAMS,
  API_GET_MY_CONTENTS_REQUEST_QUERY_PARAMS,
  API_GET_MY_CONTENTS_PROPS,
} from '../types';

export type RequestGetContentsInProgress = API_GET_MY_CONTENTS_REQUEST_PATH_PARAMS &
  API_GET_MY_CONTENTS_REQUEST_QUERY_PARAMS;
export type ResponseGetContentsInProgress = API_GET_MY_CONTENTS_PROPS['response']['body'];

export type CallParamsGetContentsInProgress = Omit<
  RequestGetContentsInProgress,
  'organizationId'
> & {
  successSaga?: CallbackSaga;
  errorSaga?: CallbackSaga;
};

export type CallResponseGetContentsInProgress = ResponseGetContentsInProgress;

type CallErrorGetContentsInProgress = {
  message: string;
  code: number;
  type: string;
  title: string;
  status: number;
};

type FullRequestType = ReturnType<typeof createGeneralCall>['selectors']['request'] & {
  payload: Omit<CallParamsGetContentsInProgress, 'successSaga' | 'errorSaga'>;
};

type CallSelectorsGetContentsInProgress = {
  data: CallResponseGetContentsInProgress['data'];
  totalItems: CallResponseGetContentsInProgress['totalItems'];
  fullRequest: FullRequestType;
};

let internalPayload = {};

const addMustHaveFilterToPayload = (
  payload: CallParamsGetContentsInProgress,
): CallParamsGetContentsInProgress => {
  const mustHaveFilter = [Filter.ISINPROGRESS];
  const payloadWithFilter = {
    ...payload,
    filters: [...(payload?.filters || []), ...mustHaveFilter],
  };
  return payloadWithFilter;
};

export const getContentsInProgressCall = createGeneralCall<
  CallParamsGetContentsInProgress,
  CallResponseGetContentsInProgress,
  CallErrorGetContentsInProgress,
  CallSelectorsGetContentsInProgress
>({
  id: KEY_GET_CONTENTS_IN_PROGRESS,
  url:
    ({ payload }) =>
    state => {
      const org = getCurrentOrganization(state);
      return API_GET_MY_CONTENTS(org as unknown as string, {
        ...addMustHaveFilterToPayload(payload),
        skillCategory: SkillCategory.SOFT_SKILL,
      });
    },
  method: 'get',
  paramsSelector:
    ({ payload }) =>
    () => {
      const payloadWithFilter = addMustHaveFilterToPayload(payload);
      internalPayload = { ...payloadWithFilter };

      return payloadWithFilter;
    },
  selectors: {
    data: ({ selectors }) => createSelector(selectors.response, res => res?.data || []),
    totalItems: ({ selectors }) => createSelector(selectors.response, res => res?.totalItems || 0),
    fullRequest: ({ selectors }) =>
      createSelector(selectors.request, request => {
        return {
          ...request,
          payload: { ...internalPayload },
        } as unknown as FullRequestType;
      }),
  },
  successMock: IS_MOCK_ENABLED() ? MOCK_RESPONSE_GET_CONTENTS_IN_PROGRESS : undefined,
  cancellable: true,
  takeEffect: takeEvery as unknown as TakeEffect,
  rsmOptions: {
    doubleRun: false,
  },
});
